/**
 * AI-Automated Hospice Medication Workflow Page
 * 
 * Main entry point for the medication workflow system.
 * Access controlled by role:
 * - Physicians: Full access to order queue and approval
 * - Nurses: MAR view and administration
 * - Admin/Ops: Dashboard and analytics
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Pill, Stethoscope, User, Activity, Package,
  ArrowLeft, Search, RefreshCw, ChevronRight,
  Settings, Brain, ClipboardCheck, Shield
} from "lucide-react";

import MedicationWorkflowDashboard from "@/components/MedicationWorkflowDashboard";
import NurseMARDashboard from "@/components/NurseMARDashboard";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MedicationWorkflowPage() {
  const { user, role } = useAuth();
  const { theme } = useTheme();
  
  const [activeView, setActiveView] = useState("dashboard");
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(true);

  // Fetch patients for MAR view
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await fetch(`${API}/api/patients`);
        if (response.ok) {
          const data = await response.json();
          setPatients(data.patients || data || []);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      } finally {
        setLoadingPatients(false);
      }
    };
    
    fetchPatients();
  }, []);

  // Filter patients by search
  const filteredPatients = patients.filter(p => 
    (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Determine default view based on role
  useEffect(() => {
    if (role === "physician") {
      setActiveView("dashboard");
    } else if (role === "staff" || role === "nurse") {
      setActiveView("mar");
    } else {
      setActiveView("dashboard");
    }
  }, [role]);

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 max-w-7xl overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`text-xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600">
                  <Pill className="h-6 w-6 text-white" />
                </div>
                AI Medication Workflow
              </h1>
              <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Automated medication recommendations • One-click approval • Zero re-entry
              </p>
            </div>
            <Badge variant="outline" className="text-sm">
              Role: {role || 'user'}
            </Badge>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardContent className="pt-4 flex items-center gap-3">
                <Brain className="h-8 w-8 text-purple-500 shrink-0" />
                <div className="min-w-0">
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>AI-Powered</p>
                  <p className={`font-bold truncate ${isDark ? 'text-white' : ''}`}>Recommendations</p>
                </div>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardContent className="pt-4 flex items-center gap-3">
                <ClipboardCheck className="h-8 w-8 text-green-500" />
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Target</p>
                  <p className={`font-bold ${isDark ? 'text-white' : ''}`}>≤30s Review</p>
                </div>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardContent className="pt-4 flex items-center gap-3">
                <Package className="h-8 w-8 text-blue-500" />
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pharmacy</p>
                  <p className={`font-bold ${isDark ? 'text-white' : ''}`}>Auto-Push</p>
                </div>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardContent className="pt-4 flex items-center gap-3">
                <Shield className="h-8 w-8 text-amber-500" />
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Compliance</p>
                  <p className={`font-bold ${isDark ? 'text-white' : ''}`}>Audit-Ready</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Navigation */}
        <Tabs value={activeView} onValueChange={setActiveView} className="space-y-6">
          <TabsList className={`grid w-full grid-cols-1 sm:grid-cols-3 h-auto ${isDark ? 'bg-gray-800' : ''}`}>
            <TabsTrigger value="dashboard" className="flex items-center gap-2 py-3">
              <Stethoscope className="h-4 w-4" />
              <span className="hidden sm:inline">Physician / Operations</span>
              <span className="sm:hidden">Physician</span>
            </TabsTrigger>
            <TabsTrigger value="mar" className="flex items-center gap-2 py-3">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Nurse MAR</span>
              <span className="sm:hidden">MAR</span>
            </TabsTrigger>
            <TabsTrigger value="pharmacy" className="flex items-center gap-2 py-3">
              <Package className="h-4 w-4" />
              Pharmacy
            </TabsTrigger>
          </TabsList>

          {/* Physician / Operations Dashboard */}
          <TabsContent value="dashboard">
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardContent className="pt-6">
                <MedicationWorkflowDashboard userRole={role} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Nurse MAR View */}
          <TabsContent value="mar">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Patient Selector */}
              <Card className={`lg:col-span-1 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDark ? 'text-white' : ''}`}>
                    Select Patient
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        placeholder="Search patients..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
                      />
                    </div>
                    
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {loadingPatients ? (
                        <div className="text-center py-4">
                          <RefreshCw className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                        </div>
                      ) : filteredPatients.length > 0 ? (
                        filteredPatients.map((patient) => (
                          <Button
                            key={patient.id}
                            variant={selectedPatient?.id === patient.id ? "default" : "outline"}
                            className={`w-full justify-between ${
                              isDark && selectedPatient?.id !== patient.id 
                                ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' 
                                : ''
                            }`}
                            onClick={() => setSelectedPatient(patient)}
                          >
                            <span className="truncate">{patient.name || patient.id}</span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        ))
                      ) : (
                        <p className={`text-center text-sm py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          No patients found
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* MAR Dashboard */}
              <Card className={`lg:col-span-3 ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
                <CardContent className="pt-6">
                  <NurseMARDashboard 
                    patientId={selectedPatient?.id}
                    patientName={selectedPatient?.name}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pharmacy View (using the dashboard's pharmacy tab) */}
          <TabsContent value="pharmacy">
            <Card className={isDark ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                  <Package className="h-5 w-5 text-blue-600" />
                  Pharmacy Command Center
                  <Badge variant="outline">MOCKED</Badge>
                </CardTitle>
                <CardDescription className={isDark ? 'text-gray-400' : ''}>
                  Order tracking and delivery SLA monitoring
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MedicationWorkflowDashboard userRole="pharmacy" />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
